/**
 * Services API pour le module Operation
 * Gère les appels API et la transformation des filtres
 */

import { FilterOptions, PaginationState, ApiOperation } from "./types";

// ============================================================================
// Configuration API
// ============================================================================

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Récupère le token d'authentification
 */
const getAuthToken = (): string | null => localStorage.getItem("token");

/**
 * Headers par défaut pour les requêtes API
 */
const getDefaultHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${getAuthToken()}`,
  "Content-Type": "application/json",
  Accept: "application/json",
});

// ============================================================================
// Transformation des filtres UI -> Paramètres API
// ============================================================================

/**
 * Interface pour les paramètres de requête API
 * Ces noms correspondent aux QueryFilters côté backend (OperationFilter.php)
 */
export interface ApiOperationFilters {
  // Pagination
  page?: number;
  per_page?: number;

  // Filtres par statut (StatusFilter)
  status_id?: string;
  status_action?: string;
  status_type_compte?: string;

  // Filtres par opération (OperationFilter)
  // Barre de recherche : terme unique, resolu cote micro-service en un OU sur le nom,
  // le numero, la reference et l'adresse de l'operation. Un seul champ de saisie ne peut
  // pas se traduire en un ET colonne par colonne : l'utilisateur y tape aussi bien un
  // libelle qu'un numero ("BC-25-206-84-540").
  search?: string;
  operation_nom?: string;
  operation_lot_technique?: string | number;
  operation_lot_departement?: string | number;
  operation_organisme_id?: string | number;
  operation_prix_total_min?: number;
  operation_prix_total_max?: number;
  operation_type_compte?: string;

  // Filtre par type (BC/MS)
  type?: string;
}

/**
 * Transforme les filtres de l'interface utilisateur en paramètres API
 *
 * Mapping:
 * - lotTechnique -> operation_lot_technique
 * - lotGeographique -> operation_lot_departement
 * - nomOperation -> operation_nom
 * - client -> operation_organisme_id (note: nécessite l'ID, pas le nom)
 * - prixRange.min -> operation_prix_total_min
 * - prixRange.max -> operation_prix_total_max
 * - status -> status_id
 * - type -> type (BC/MS)
 */
export const transformFiltersToApiParams = (
  filters: Partial<FilterOptions>,
  page: number = 1,
  perPage: number = 100
): ApiOperationFilters => {
  const params: ApiOperationFilters = {
    page,
    per_page: perPage,
  };

  // Filtre par nom d'opération
  if (filters.nomOperation?.trim()) {
    params.operation_nom = filters.nomOperation.trim();
  }

  // Filtre par lot technique
  // Note: Le composant FiltreDeCommande envoie le format "ID - Nom"
  // Il faut extraire l'ID si c'est le cas
  if (filters.lotTechnique?.trim()) {
    const lotTechMatch = filters.lotTechnique.match(/^(\d+)/);
    if (lotTechMatch) {
      params.operation_lot_technique = parseInt(lotTechMatch[1], 10);
    } else {
      params.operation_lot_technique = filters.lotTechnique;
    }
  }

  // Filtre par département (lot géographique)
  if (filters.lotGeographique?.trim()) {
    params.operation_lot_departement = filters.lotGeographique;
  }

  // Filtre par entité (organisme pour prestataire, entité pour acheteur)
  if (filters.entite?.trim()) {
    params.operation_client = filters.entite;
  }

  // Filtre par plage de prix
  if (filters.prixRange) {
    if (filters.prixRange.min > 0) {
      params.operation_prix_total_min = filters.prixRange.min;
    }
    if (filters.prixRange.max > 0 && filters.prixRange.max < Infinity) {
      params.operation_prix_total_max = filters.prixRange.max;
    }
  }

  // Filtre par statut
  if (filters.status?.trim()) {
    params.status_id = filters.status;
  }

  // Filtre par type (BC/MS)
  if (
    filters.type &&
    filters.type !== "Tous" &&
    filters.type !== "Tous les types"
  ) {
    params.type = filters.type;
  }

  return params;
};

/**
 * Construit l'URL avec les paramètres de requête
 */
export const buildQueryString = (params: ApiOperationFilters): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

// ============================================================================
// Appels API
// ============================================================================

export interface FetchOperationsResponse {
  status: "success" | "error";
  data: ApiOperation[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
  message?: string;
}

/**
 * Récupère les opérations avec filtres
 */
export const fetchOperationsApi = async (
  params: ApiOperationFilters,
  signal?: AbortSignal
): Promise<FetchOperationsResponse> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("Aucun token d'authentification trouvé");
  }

  const queryString = buildQueryString(params);
  const url = `${API_URL}/operation?${queryString}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getDefaultHeaders(),
    signal,
  });

  if (!response.ok) {
    throw new Error(
      `Erreur lors de la récupération des opérations: ${response.statusText}`
    );
  }

  const data = await response.json();

  if (data.status !== "success") {
    throw new Error(data.message || "Erreur API");
  }

  return data;
};

/**
 * Recherche des opérations par nom
 */
export const searchOperationsApi = async (
  query: string,
  signal?: AbortSignal
): Promise<FetchOperationsResponse> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("Aucun token d'authentification trouvé");
  }

  const url = `${API_URL}/operation?operation_nom=${encodeURIComponent(query)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getDefaultHeaders(),
    signal,
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la recherche");
  }

  return response.json();
};

/**
 * Récupère les archives
 */
export const fetchArchivesApi = async (
  params: Record<string, any> = {},
  signal?: AbortSignal
): Promise<any> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("Aucun token d'authentification trouvé");
  }

  const query = new URLSearchParams(params).toString();
  const url = `${API_URL}/bdc-archive?${query}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getDefaultHeaders(),
    signal,
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la récupération des archives");
  }

  return response.json();
};

/**
 * Récupère les paniers importés
 */
export const fetchPaniersImportesApi = async (
  page: number = 1,
  signal?: AbortSignal
): Promise<any> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("Aucun token d'authentification trouvé");
  }

  const url = `${API_URL}/bdci/operations?page=${page}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getDefaultHeaders(),
    signal,
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la récupération des paniers importés");
  }

  return response.json();
};

/**
 * Récupère le compteur des actions requises
 */
export const fetchActionRequiseCountApi = async (): Promise<{
  count: number;
  count_bdci: number;
}> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("Aucun token d'authentification trouvé");
  }

  const response = await fetch(
    `${API_URL}/operations-compteur?status_action=1`,
    {
      method: "GET",
      headers: getDefaultHeaders(),
    }
  );

  const result = await response.json();

  if (result.status === "success" && result.data) {
    return {
      count: result.data.count ?? 0,
      count_bdci: result.data.count_bdci ?? 0,
    };
  }

  return { count: 0, count_bdci: 0 };
};

/**
 * Exporte toutes les opérations en CSV
 */
export const exportOperationsApi = async (): Promise<Blob> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("Aucun token d'authentification trouvé");
  }

  const response = await fetch(`${API_URL}/operations/exportOperations`, {
    method: "GET",
    headers: getDefaultHeaders(),
  });

  if (!response.ok) {
    throw new Error("Erreur lors de l'exportation");
  }

  return response.blob();
};

/**
 * Télécharge un document signé
 */
export const downloadSignedDocumentApi = async (
  externalId: string,
  source: string
): Promise<Blob> => {
  const response = await fetch(`${API_URL}/documents/signed-contrat`, {
    method: "POST",
    headers: {
      ...getDefaultHeaders(),
      Accept: "application/pdf",
    },
    body: JSON.stringify({
      external_id: externalId,
      external_type: "operation",
      source,
    }),
  });

  if (!response.ok) {
    throw new Error("Erreur lors du téléchargement");
  }

  return response.blob();
};

/**
 * Télécharge une archive PDF
 */
export const downloadArchivePdfApi = async (id: string): Promise<Blob> => {
  const token = getAuthToken();

  const response = await fetch(`${API_URL}/bdc-archive/${id}/download`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error("Erreur téléchargement archive");
  }

  return response.blob();
};

/**
 * Refuse une opération
 */
export const refuseOperationApi = async (
  operationId: string
): Promise<void> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("Aucun token d'authentification trouvé");
  }

  const response = await fetch(
    `${API_URL}/subsequents/${operationId}/refuser`,
    {
      method: "POST",
      headers: getDefaultHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error("Erreur lors du refus de l'opération");
  }
};

/**
 * Relance un signataire
 */
export const relancerSignataireApi = async (
  operationId: string
): Promise<void> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("Aucun token d'authentification trouvé");
  }

  const response = await fetch(
    `${API_URL}/subsequents/operation-ms/${operationId}/relancer-signataire`,
    {
      method: "POST",
      headers: getDefaultHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error("Erreur lors de la relance");
  }
};
